<template>
	<!-- This classes only function is to make sure all autocompletes have the same base settings applied. Thus sayith the Jacob RawR! -->
	<v-autocomplete
		ref="autocompleteRef"
		v-bind="$attrs"
		hide-details="auto"
		variant="outlined"
		:auto-select-first="props.autoSelectFirst"
		item-value="id"
		:return-object="props.returnObject"
		clear-on-select
		:chips="!!$attrs.multiple"
		:deletable-chips="!!$attrs.multiple"
		:no-filter="true">
		<!-- Bold the trailing (Required)/(Optional) tag on the label (LSP-16777).
		     The label arrives via $attrs; render it through FieldLabel unless the
		     caller provides its own #label slot. No-op for tag-less labels. -->
		<template v-if="$attrs.label && !$slots.label" #label>
			<FieldLabel :label="String($attrs.label)" />
		</template>
		<template v-if="!!$attrs.multiple && !$slots.chip" #chip="{ item, props: chipProps }">
			<v-tooltip location="top">
				<template #activator="{ props: tooltipProps }">
					<div v-bind="tooltipProps" class="chip-wrapper">
						<v-chip closable :color="chipColor" close-icon="icon-close-medium" v-bind="chipProps">
							<span class="text-truncate">{{ getChipText(item) }}</span>
							<template v-if="props.chipEditable" #append>
								<v-icon class="cursor-pointer ms-1" @click.stop="emit('edit-chip', item.raw)"
									>icon-pencil-edit-solid</v-icon
								>
							</template>
						</v-chip>
					</div>
				</template>
				<span>{{ getChipText(item) }}</span>
			</v-tooltip>
		</template>
		<!-- Forward all slots -->
		<template v-for="(_, slot) in $slots" #[slot]="scope" :key="slot">
			<slot :name="slot" v-bind="scope || {}"></slot>
		</template>
	</v-autocomplete>
</template>

<script setup lang="ts">
	import { ref } from "vue";
	import FieldLabel from "@common-ui/components/FieldLabel.vue";

	interface Props {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		// Vuetify's autocomplete chip slot provides an item object with structure { title?: string, raw?: T }
		// where T can be any entity type (Customer, User, Project, etc.). Since this base component
		// must work generically with all entity types, we cannot use a more specific type.
		chipText?: (item: any) => string;
		chipColor?: string;
		chipEditable?: boolean;
		returnObject?: boolean;
		autoSelectFirst?: boolean | "exact";
	}

	const props = withDefaults(defineProps<Props>(), {
		chipText: undefined,
		chipColor: "grey",
		chipEditable: false,
		returnObject: true,
		autoSelectFirst: true
	});

	const emit = defineEmits<{
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		"edit-chip": [item: any];
	}>();

	const autocompleteRef = ref<any>(null);

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	// Vuetify's autocomplete chip slot provides an item object with structure { title?: string, raw?: T }
	// where T can be any entity type (Customer, User, Project, etc.). Since this base component
	// must work generically with all entity types, we cannot use a more specific type.
	function getChipText(item: any): string {
		if (props.chipText) {
			return props.chipText(item);
		}
		return item.title || item.name || item.raw?.name || item.raw?.title || "";
	}

	function getActivatorElement(): HTMLElement | null {
		const rootElement = autocompleteRef.value?.$el;
		return rootElement && typeof rootElement.getBoundingClientRect === "function" ? rootElement : null;
	}

	defineExpose({
		blur: () => autocompleteRef.value?.blur?.(),
		getActivatorElement
	});
</script>

<style scoped>
	/* LSP-19649: the chip sits inside `.v-field__input`, which Vuetify paints `cursor: text`
	   (VAutocomplete.css: `.v-autocomplete .v-field .v-field__input`). Inheritance gave the chip
	   body and its label the text I-beam, as if the tag were selectable copy. The chip body does
	   nothing, so it takes the ordinary arrow; the two affordances that DO act keep their pointer
	   from their own rules (`.cursor-pointer` on the pencil, Vuetify's `.v-chip__close` on the X).
	   `:not(.v-chip--link)` so a genuinely clickable chip elsewhere is never flattened, and scoped to
	   `.v-autocomplete__selection` so it cannot reach the chips the dropdown renders inside a
	   clickable `v-list-item` (Bugbot, commit bd4db2d): those correctly inherit the row's pointer,
	   because clicking that row really does select the tag. */
	.v-autocomplete__selection .v-chip:not(.v-chip--link) {
		cursor: default;
	}

	.chip-wrapper {
		display: inline-block;
		max-width: 150px;
	}
</style>
<!-- TODO: There is a better way to do this in the Vuetify config, but it would apply everywhere to all text fields, and thats too much testing before the 15th. -->
